<template>
  <header class="header" :class="{ 'header--lifted': lifted, 'header--over': overlays && !lifted }">
    <nav class="header__bar" :aria-label="t('navigation.primary')">
      <router-link to="/" class="brand" :aria-label="t('navigation.brandHome')">
        <BrandMark class="brand__mark" />
        <span class="brand__name">Dimension&#8209;X</span>
      </router-link>

      <ul class="nav">
        <li v-for="link in links" :key="link.id" class="nav__item">
          <router-link
            :to="link.url"
            class="nav__link"
            :class="{ 'is-current': isActiveLink(link.url) }"
            :aria-current="isActiveLink(link.url) ? 'page' : undefined"
          >{{ t(`navigation.${link.id}`) }}</router-link>
        </li>
      </ul>

      <div class="header__actions">
        <LanguageSwitcher />
        <UserMenu v-if="isAuthenticated" />
        <template v-else>
          <router-link class="header__login" to="/login">{{ t('navigation.login') }}</router-link>
          <!--
            The register button is deliberately quiet while the hero is on
            screen: the hero already owns the one accent-filled action, and two
            identical green buttons in one viewport is what made neither of them
            read as primary. It promotes to accent once the hero has scrolled
            away and its CTA is gone.
          -->
          <router-link
            class="header__register"
            :class="promoted ? 'header__register--accent' : 'header__register--quiet'"
            to="/register"
          >{{ t('navigation.register') }}</router-link>
        </template>
      </div>

      <button
        class="burger"
        type="button"
        :aria-label="t('navigation.toggleMenu')"
        :aria-expanded="isMobileMenuOpen ? 'true' : 'false'"
        aria-controls="mobile-menu"
        @click="toggleMobileMenu"
      >
        <span class="burger__box" :class="{ 'is-open': isMobileMenuOpen }">
          <span class="burger__line"></span>
          <span class="burger__line"></span>
        </span>
      </button>
    </nav>

    <transition name="sheet">
      <div v-if="isMobileMenuOpen" class="sheet" @click="closeMobileMenu">
        <div
          id="mobile-menu"
          ref="sheetPanel"
          class="sheet__panel"
          role="dialog"
          aria-modal="true"
          :aria-label="t('navigation.mobileMenuTitle')"
          @click.stop
          @keydown.tab="trapFocus"
        >
          <ul class="sheet__nav">
            <li v-for="link in links" :key="link.id">
              <router-link
                :to="link.url"
                class="sheet__link"
                :class="{ 'is-current': isActiveLink(link.url) }"
                @click="closeMobileMenu"
              >{{ t(`navigation.${link.id}`) }}</router-link>
            </li>
          </ul>

          <div class="sheet__actions">
            <template v-if="isAuthenticated">
              <LandingButton variant="accent" block :to="{ name: 'PlayerHomeDashboard' }" @click="closeMobileMenu">
                {{ t('navigation.dashboard') }}
              </LandingButton>
              <LandingButton v-if="isGameMaster" variant="ghost" block :to="{ name: 'GameMaster' }" @click="closeMobileMenu">
                {{ t('navigation.gameMaster') }}
              </LandingButton>
              <LandingButton variant="ghost" block :action="signOut">
                {{ t('navigation.logout') }}
              </LandingButton>
            </template>
            <template v-else>
              <LandingButton variant="accent" block :to="{ name: 'Register' }" @click="closeMobileMenu">
                {{ t('navigation.register') }}
              </LandingButton>
              <LandingButton variant="ghost" block :to="{ name: 'Login' }" @click="closeMobileMenu">
                {{ t('navigation.login') }}
              </LandingButton>
            </template>
          </div>

          <div class="sheet__foot">
            <LanguageSwitcher />
          </div>
        </div>
      </div>
    </transition>
  </header>
</template>

<script>
import LanguageSwitcher from './LanguageSwitcher.vue';
import BrandMark from './BrandMark.vue';
import LandingButton from './btn/LandingButton.vue';
import UserMenu from './public/UserMenu.vue';
import ClientManagerService from '@/services/clientManagerService.js';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

export default {
  components: { LanguageSwitcher, BrandMark, LandingButton, UserMenu },
  setup() {
    const { t } = useI18n();
    const route = useRoute();
    const store = useStore();
    const isAuthenticated = computed(() => store.getters.isAuthenticated);
    const isGameMaster = computed(() => store.getters.isGameMaster);
    return { t, route, store, isAuthenticated, isGameMaster };
  },
  data() {
    return {
      isMobileMenuOpen: false,
      lifted: false,
      lastFocused: null,
      links: [
        { id: 'about', url: '/faq/what-is-it' },
        { id: 'world', url: '/world' },
        { id: 'community', url: '/community' },
        { id: 'help', url: '/faq' },
        { id: 'roll20', url: '/roll20' }
      ]
    };
  },
  computed: {
    /* Only the landing has artwork behind the header to sit over. Every other
     * page starts on the page surface, so the bar is opaque from the top —
     * otherwise its links float on nothing. */
    overlays() {
      return this.route.name === 'Home';
    },
    /*
     * When the header's Register button is allowed to be the accent-filled one.
     *
     * Only on the landing, and only once the hero has scrolled away. Every
     * other public page owns its own primary action — Login and Register have
     * an accent submit, 404 has an accent "back home", Community has an accent
     * "join Discord" — and a permanently accent-filled button in the chrome
     * put a second one on screen beside each of them, which is the exact
     * failure the rationing rule exists to prevent. Persistent chrome does not
     * get to be the loudest thing on a page it knows nothing about.
     */
    promoted() {
      return this.overlays && this.lifted;
    },
    /*
     * The most specific matching link, not every matching link.
     *
     * `/faq/what-is-it` satisfies the prefix test for both "About the game"
     * (/faq/what-is-it) and "FAQ" (/faq), so two of the four nav items were
     * marked current at once — two underlines, and `aria-current="page"`
     * announced twice. Longest match wins.
     */
    activeUrl() {
      const path = this.route.path;
      return this.links
        .filter((link) => path === link.url || path.startsWith(`${link.url}/`))
        .sort((a, b) => b.url.length - a.url.length)
        .map((link) => link.url)[0] ?? null;
    }
  },
  methods: {
    /*
     * The sheet claims role="dialog" aria-modal="true", which is a promise to
     * assistive tech that focus is contained and will be handed back. It was
     * making that promise with no focus management at all: opening the menu
     * left focus on the burger behind the overlay, and tabbing walked straight
     * out into the page underneath. Same contract as Lightbox.vue.
     */
    toggleMobileMenu() {
      if (this.isMobileMenuOpen) {
        this.closeMobileMenu();
        return;
      }
      this.isMobileMenuOpen = true;
      document.body.style.overflow = 'hidden';
      this.lastFocused = document.activeElement;
      this.$nextTick(() => {
        this.$refs.sheetPanel?.querySelector('a, button')?.focus();
      });
    },
    closeMobileMenu() {
      if (!this.isMobileMenuOpen) return;
      this.isMobileMenuOpen = false;
      document.body.style.overflow = '';
      const target = this.lastFocused;
      this.lastFocused = null;
      this.$nextTick(() => {
        if (target && document.contains(target)) target.focus();
      });
    },
    trapFocus(event) {
      const focusable = this.$refs.sheetPanel?.querySelectorAll(
        'a[href], button, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusable?.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    isActiveLink(url) {
      return this.activeUrl === url;
    },
    /*
     * The cache is keyed to nothing but the process, so without this the next
     * user signing in on the same tab reads the previous user's payload.
     */
    signOut() {
      this.closeMobileMenu();
      ClientManagerService.clearClientInfoCache();
      this.store.dispatch('logout');
      this.$router.push({ name: 'Home' });
    },
    handleKeydown(event) {
      if (event.key === 'Escape' && this.isMobileMenuOpen) this.closeMobileMenu();
    },
    handleScroll() {
      this.lifted = window.scrollY > 24;
    }
  },
  watch: {
    $route() {
      this.closeMobileMenu();
    }
  },
  mounted() {
    document.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    this.handleScroll();
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.handleKeydown);
    window.removeEventListener('scroll', this.handleScroll);
    document.body.style.overflow = '';
  }
};
</script>

<style scoped>
/*
 * Rebuilt onto tokens.css. This component previously carried its own font
 * stack (-apple-system), its own greys and its own two button styles at a
 * different height from every other button on the site — which is most of why
 * the chrome and the page read as two different products.
 */
.header {
  position: sticky;
  top: 0;
  z-index: var(--z-chrome);
  transition: background-color var(--duration-slow) var(--ease),
              border-color var(--duration-slow) var(--ease),
              backdrop-filter var(--duration-slow) var(--ease);
  border-bottom: 1px solid transparent;
}

/* Over the hero: no bar at all, so the artwork runs to the top of the window
 * and the first screen reads as one frame rather than a strip of chrome above
 * a photograph. The text keeps its own shadow for legibility. */
.header--over {
  background-color: transparent;
}

.header--over::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(4, 7, 11, 0.72), transparent);
  pointer-events: none;
}

.header--lifted {
  background-color: rgba(8, 11, 16, 0.86);
  backdrop-filter: blur(14px);
  border-bottom-color: var(--border);
}

.header__bar {
  max-width: var(--page-max-wide);
  height: var(--header-h);
  margin: 0 auto;
  padding: 0 var(--page-gutter);
  display: flex;
  align-items: center;
  gap: var(--space-6);
}

/* ---- Brand ---------------------------------------------------------- */
.brand {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3);
  flex: none;
  color: var(--text);
  text-decoration: none;
}

.brand__mark {
  width: 26px;
  height: 26px;
  flex: none;
  color: var(--accent);
  transition: transform var(--duration) var(--ease);
}

.brand:hover .brand__mark {
  transform: rotate(90deg);
}

.brand__name {
  font-family: var(--font-display);
  font-size: 0.95rem;
  font-weight: var(--weight-medium);
  /* The wordmark is the one place tracking is applied on purpose: it is a
   * logotype, not a heading, and Orbitron's Latin metrics carry it. */
  letter-spacing: 0.08em;
  white-space: nowrap;
  color: var(--text);
}

.brand:hover .brand__name,
.brand:focus-visible .brand__name {
  color: var(--text);
}

/* ---- Nav ------------------------------------------------------------- */
.nav {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  list-style: none;
  margin: 0 auto;
  padding: 0;
}

.nav__item {
  margin: 0;
}

.nav__link {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-sm);
  /* Neutral, not accent. Four cyan nav links plus a cyan button plus cyan
   * headings is what made the accent meaningless. */
  color: var(--text-muted);
  font-size: var(--text-sm);
  font-weight: var(--weight-medium);
  text-decoration: none;
  white-space: nowrap;
  position: relative;
  transition: color var(--duration) var(--ease);
}

.nav__link:hover {
  color: var(--text);
  text-decoration: none;
}

.nav__link.is-current {
  color: var(--text);
}

/* The accent survives here as a 1px rule — enough to mark position, not
 * enough to compete with the button. */
.nav__link.is-current::after {
  content: '';
  position: absolute;
  left: var(--space-4);
  right: var(--space-4);
  bottom: -2px;
  height: 1px;
  background: var(--accent-line);
}

/* ---- Actions --------------------------------------------------------- */
.header__actions {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex: none;
}

.header__login,
.header__register {
  display: inline-flex;
  align-items: center;
  height: 34px;
  padding: 0 var(--space-4);
  border-radius: var(--radius-sm);
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  text-decoration: none;
  white-space: nowrap;
  transition: color var(--duration) var(--ease),
              background-color var(--duration) var(--ease),
              border-color var(--duration) var(--ease);
}

.header__login {
  color: var(--text-muted);
  padding: 0 var(--space-2);
}

.header__login:hover {
  color: var(--text);
  text-decoration: none;
}

/* Outlined, so it is unmistakably a button without being an accent surface.
 * The accent survives as the text colour: enough to mark it as the sign-up
 * path, not enough to compete with a filled button belonging to the page. */
.header__register--quiet {
  color: var(--accent);
  border: 1px solid var(--border-accent);
}

.header__register--quiet:hover {
  border-color: var(--accent);
  background: var(--accent-wash);
  color: var(--accent-strong);
  text-decoration: none;
}

/* Over the hero photograph a dark-slate border is invisible and the accent
 * text loses contrast against bright artwork, so this variant borrows light
 * from the picture instead of trying to sit on top of it. */
.header--over .header__register--quiet {
  color: var(--text);
  border-color: rgba(255, 255, 255, 0.3);
  background: rgba(255, 255, 255, 0.07);
  backdrop-filter: blur(4px);
}

.header--over .header__register--quiet:hover {
  border-color: var(--accent-line);
  background: rgba(255, 255, 255, 0.13);
  color: var(--text);
}

.header__register--accent {
  background: var(--accent);
  color: var(--text-on-accent);
  border: 1px solid var(--accent);
}

.header__register--accent:hover {
  background: var(--accent-strong);
  border-color: var(--accent-strong);
  color: var(--text-on-accent);
  text-decoration: none;
}

/* ---- Burger ---------------------------------------------------------- */
.burger {
  display: none;
  background: none;
  border: none;
  padding: var(--space-2);
  margin-left: auto;
  cursor: pointer;
}

.burger__box {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  width: 22px;
  height: 14px;
}

.burger__line {
  display: block;
  height: 1.5px;
  width: 100%;
  background: var(--text);
  border-radius: 2px;
  transition: transform var(--duration) var(--ease), opacity var(--duration) var(--ease);
}

.burger__box.is-open .burger__line:first-child {
  transform: translateY(3.75px) rotate(45deg);
}

.burger__box.is-open .burger__line:last-child {
  transform: translateY(-3.75px) rotate(-45deg);
}

/* ---- Mobile sheet ---------------------------------------------------- */
.sheet {
  position: fixed;
  inset: 0;
  top: var(--header-h-sm);
  background: rgba(4, 7, 11, 0.6);
  z-index: var(--z-overlay);
}

.sheet__panel {
  background: rgba(8, 11, 16, 0.98);
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border);
  padding: var(--space-4) var(--page-gutter) var(--space-6);
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  max-height: calc(100dvh - var(--header-h-sm));
  overflow-y: auto;
}

.sheet__nav {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
}

.sheet__nav li {
  margin: 0;
}

.sheet__link {
  display: block;
  padding: var(--space-4) 0;
  border-bottom: 1px solid var(--border);
  color: var(--text-muted);
  font-size: var(--text-lg);
  text-decoration: none;
}

.sheet__link.is-current {
  color: var(--text);
}

.sheet__actions {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.sheet__foot {
  display: flex;
  justify-content: center;
}

.sheet-enter-active,
.sheet-leave-active {
  transition: opacity var(--duration) var(--ease);
}

.sheet-enter-active .sheet__panel,
.sheet-leave-active .sheet__panel {
  transition: transform var(--duration) var(--ease);
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}

.sheet-enter-from .sheet__panel,
.sheet-leave-to .sheet__panel {
  transform: translateY(-8px);
}

/* ---- Responsive ------------------------------------------------------
 * One breakpoint. The old header had four (1200/1100/900/768) that shrank
 * the type twice before finally switching to the burger, so between 900 and
 * 1100px the nav ran at 13.6px against a 15px button beside it.
 */
@media (max-width: 62rem) {
  .header__bar {
    height: var(--header-h-sm);
    gap: var(--space-4);
  }

  .nav,
  .header__actions {
    display: none;
  }

  .burger {
    display: block;
  }
}
</style>
